import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { FetchStrategy, GetOptions } from "~/environment/RecordLoader";
import { getMessagesScheduledToBeSent } from "./getMessagesScheduledToBeSent";
import { RecordValue } from "libs/schema";

export type GetLastSentOrScheduledMessageInThreadDetailsResult = Pick<
  RecordValue<"message">,
  "id" | "timeline_order" | "sent_at"
> | null;

type GetLastSentOrScheduledMessageInThreadDetailsProps = {
  threadId: string;
  getThreadFetchStrategy?: FetchStrategy;
  getMessagesScheduledToBeSentFetchStrategy?: FetchStrategy;
};

/**
 * Gets last message in a thread while considering messages which are scheduled to be sent
 * but haven't been sent yet. This is necessary because the `last_message_*` properties
 * of a thread are not updated until a message is delivered by the server.
 */
export async function getLastSentOrScheduledMessageInThreadDetails(
  environment: ClientEnvironment,
  props: GetLastSentOrScheduledMessageInThreadDetailsProps,
  options?: GetOptions,
): Promise<GetLastSentOrScheduledMessageInThreadDetailsResult> {
  const [[thread], scheduledMessages] = await Promise.all([
    environment.recordLoader.getRecord("thread", props.threadId, {
      ...options,
      fetchStrategy: props.getThreadFetchStrategy ?? options?.fetchStrategy,
    }),
    getMessagesScheduledToBeSent(environment, {
      ...options,
      fetchStrategy: props.getMessagesScheduledToBeSentFetchStrategy ?? options?.fetchStrategy,
    }),
  ]);

  const lastScheduledMessage = scheduledMessages.findLast((m) => m.thread_id === props.threadId);

  if (lastScheduledMessage) {
    if (thread) {
      if (lastScheduledMessage.timeline_order > thread.last_message_timeline_order) {
        return {
          id: lastScheduledMessage.id,
          timeline_order: lastScheduledMessage.timeline_order,
          sent_at: lastScheduledMessage.sent_at,
        };
      }

      return {
        id: thread.last_message_id,
        timeline_order: thread.last_message_timeline_order,
        sent_at: thread.last_message_sent_at,
      };
    }

    return {
      id: lastScheduledMessage.id,
      timeline_order: lastScheduledMessage.timeline_order,
      sent_at: lastScheduledMessage.sent_at,
    };
  }

  if (thread) {
    return {
      id: thread.last_message_id,
      timeline_order: thread.last_message_timeline_order,
      sent_at: thread.last_message_sent_at,
    };
  }

  return null;
}
